// libs
import { useState, useEffect } from "react";
import { BarChart2 } from "lucide-react";

// components
import CustomButton from "../../components/CustomButton";
import StatisticInfo from "../../components/StatisticInfo";
import NoAccess from "../../components/NoAccess";

// custom hooks
import useToken from "../../hooks/useToken";
import useUserData from "../../hooks/useUserData";

// utils
import API from "../../utils/api";

// styles
import classes from "./styles.module.css";

export default function StatisticsPage() {
  const [isPending, setIsPending] = useState(false);
  const [token] = useToken();
  const [money, setMoney] = useState({
    purchase: 0,
    sell: 0,
    investment: 0,
    conclusion: 0,
  });
  const [activeButtonNum, setActiveButtonNum] = useState(0);
  const { isAdmin } = useUserData();

  const fetchData = async (fromDate, toDate) => {
    if (isPending) return;

    setIsPending(true);
    const res = await API.getToken(
      `/statistics/range/${fromDate}/${toDate}`,
      token,
    );
    setIsPending(false);

    if (res.status === 200) {
      setMoney(res.data.data.money);
    }
  };

  // day
  const onClickDay = () => {
    const todayAtNight = new Date();
    todayAtNight.setHours(0, 0, 0, 0);
    const todayNow = new Date();
    fetchData(
      todayAtNight.toLocaleString("sv-SE"),
      todayNow.toLocaleString("sv-SE"),
    );
    setActiveButtonNum(0);
  };

  // week
  const onClickWeek = () => {
    const todayAtNight = new Date();
    todayAtNight.setHours(0, 0, 0, 0);
    todayAtNight.setDate(todayAtNight.getDate() - todayAtNight.getDay() + 1);
    const todayNow = new Date();
    fetchData(
      todayAtNight.toLocaleString("sv-SE"),
      todayNow.toLocaleString("sv-SE"),
    );
    setActiveButtonNum(1);
  };

  // month
  const onClickMonth = () => {
    const todayAtNight = new Date();
    todayAtNight.setHours(0, 0, 0, 0);
    todayAtNight.setDate(1);
    const todayNow = new Date();
    fetchData(
      todayAtNight.toLocaleString("sv-SE"),
      todayNow.toLocaleString("sv-SE"),
    );
    setActiveButtonNum(2);
  };

  // year
  const onClickYear = () => {
    const todayAtNight = new Date();
    todayAtNight.setHours(0, 0, 0, 0);
    todayAtNight.setMonth(0, 1);
    const todayNow = new Date();
    fetchData(
      todayAtNight.toLocaleString("sv-SE"),
      todayNow.toLocaleString("sv-SE"),
    );
    setActiveButtonNum(3);
  };

  // all of time
  const onClickFull = () => {
    fetchData(
      new Date("2026-04-01").toLocaleString("sv-SE"),
      new Date().toLocaleString("sv-SE"),
    );
    setActiveButtonNum(4);
  };

  useEffect(() => {
    if (isAdmin) {
      onClickDay();
    }
  }, []);

  const { purchase, sell, investment, conclusion } = money;
  const marginality =
    sell > 0 && purchase > 0 ? Math.round((100 * (sell - purchase)) / sell) : 0;
  const profit = sell - purchase;
  const incirculation = investment + sell + conclusion + purchase;
  const clear = investment + sell - conclusion - purchase;

  return (
    <div className={classes.root}>
      <div className={classes.header}>
        <div className={classes.headText}>
          <BarChart2 stroke="#009966" />
          <p>Финансовый анализ</p>
        </div>
      </div>

      {isAdmin ? (
        <div className={classes.content}>
          <div className={classes.tabChecker}>
            <CustomButton
              className={`${classes.tabButton} ${activeButtonNum === 0 && classes.tabButtonActive}`}
              text="День"
              onClick={onClickDay}
            />
            <CustomButton
              className={`${classes.tabButton} ${activeButtonNum === 1 && classes.tabButtonActive}`}
              text="Неделя"
              onClick={onClickWeek}
            />
            <CustomButton
              className={`${classes.tabButton} ${activeButtonNum === 2 && classes.tabButtonActive}`}
              text="Месяц"
              onClick={onClickMonth}
            />
            <CustomButton
              className={`${classes.tabButton} ${activeButtonNum === 3 && classes.tabButtonActive}`}
              text="Год"
              onClick={onClickYear}
            />
            <CustomButton
              className={`${classes.tabButton} ${activeButtonNum === 4 && classes.tabButtonActive}`}
              text="Все время"
              onClick={onClickFull}
            />
          </div>

          <div className={classes.infoBox}>
            <StatisticInfo
              title="Кассовый поток"
              value={`${sell} ֏`}
              valueStyles={{ color: "#432DDA", background: "#f4f2ff" }}
            />
            <StatisticInfo
              title="Маржинальность"
              value={`${marginality || 0} %`}
              valueStyles={{
                color: "#009966",
                background: "#ECFDF5",
                padding: "5px 10px",
              }}
            />
            <StatisticInfo
              title="Выручка"
              value={`${profit} ֏`}
              valueStyles={{
                color: "#0976f1",
                background: "#e7f2ff",
              }}
            />
            <StatisticInfo
              title="Закупка"
              value={`${purchase} ֏`}
              valueStyles={{ color: "#C70036", background: "#ffeef2" }}
            />
            <StatisticInfo
              title="Вложено"
              value={`${investment} ֏`}
              valueStyles={{
                color: "#da19d0",
                background: "#ffeffe",
                padding: "5px 10px",
              }}
            />
            <StatisticInfo
              title="Выведено"
              value={`${conclusion} ֏`}
              valueStyles={{
                color: "#EB9F4E",
                background: "#FFFBEB",
                padding: "5px 10px",
              }}
            />
            <StatisticInfo
              title="В обороте"
              value={`${incirculation} ֏`}
              valueStyles={{
                background: "#0001",
              }}
            />
            <StatisticInfo
              title="Чистые"
              value={`${clear} ֏`}
              valueStyles={{
                color: "#124d39",
                background: "#43ecb43a",
              }}
            />
          </div>
        </div>
      ) : (
        <NoAccess />
      )}
    </div>
  );
}
